import { AlertProps } from '@business/interfaces/alert';
import { FC, useEffect, useRef } from 'react';
import { SVGAssets } from '@business/assets/vectors';
import { Button } from '../Button';
import { getIcon } from './Alert';

export const AlertModel: FC<AlertProps> = ({
  type = 'success',
  heading,
  subHeading,
  wrapperClass = '',
  contentClass = 'w-full',
  onClose = () => {},
  isClosable = true,
  isFocusable = true,
  autoHide = true,
  autoHideDuration = 10000,
  customLeftIcon,
  customRightIcon,
  children
}) => {
  const alertRef = useRef<HTMLDivElement>(null);
  const icon = customLeftIcon || getIcon(type, 50);

  useEffect(() => {
    if (isFocusable && alertRef.current) {
      alertRef.current.focus();
    }
    if (autoHide) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
      ref={alertRef}
      data-testid={`alert-modal-${type}`}>
      <div
        className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-lg w-full mx-4 p-6 flex flex-col items-center ${wrapperClass}`}>
        <div className="absolute top-4 right-4">
          {customRightIcon ||
            (isClosable && (
              <Button
                btnStyle="link"
                testid="close-button"
                className="ml-4 text-gray-400 hover:text-gray-700 dark:hover:text-white focus:outline-none"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                data-testid="close-icon">
                <SVGAssets.CloseBtn height={20} width={20} />
              </Button>
            ))}
        </div>
        <div className="flex items-center justify-center mb-3">{icon}</div>
        <div className="flex items-center w-full">
          <div className="ml-3 flex-1 text-center">
            <div className="font-semibold text-lg text-gray-900 dark:text-white">
              {heading}
            </div>
            {subHeading && (
              <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {subHeading}
              </div>
            )}
          </div>
        </div>
        <div className={`mt-4 w-full ${contentClass}`}>{children}</div>
      </div>
    </div>
  );
};
